import React, { useState } from "react";
import "./Login.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "http://localhost:5000"
    : "https://api.quote.softvolute.in");

// ============================================================
// ROLE NORMALIZER
// ============================================================

const normalizeRole = (value = "") => {
  return String(value).trim().toLowerCase().replace(/\s+/g, "_");
};

// ============================================================
// SMALL ICONS
// ============================================================

const SofaIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 11V8a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v3" />
    <path d="M3 12a2 2 0 0 1 4 0v3h10v-3a2 2 0 0 1 4 0v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
    <path d="M6 18v2M18 18v2" />
  </svg>
);

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="14" rx="3" />
    <path d="m4 7 8 6 8-6" />
  </svg>
);

const LockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4" y="10" width="16" height="11" rx="3" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m5 12 5 5 9-10" />
  </svg>
);

const AlertIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16.5h.01" />
  </svg>
);

const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

// ============================================================
// LOGIN
// ============================================================

const Login = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // ============================================================
  // FETCH ROLE PERMISSIONS
  // ============================================================

  const fetchRolePermissions = async (token, user) => {
    try {
      const userRole = normalizeRole(
        user?.role || user?.roleKey || user?.roleName || "",
      );

      console.log("Logged in role:", userRole);

      // Admin has automatic full access through canAccess()
      // so role permissions are not mandatory for admin.
      if (userRole === "admin") {
        localStorage.setItem("permissions", JSON.stringify({}));

        return {};
      }

      const res = await fetch(`${API_BASE_URL}/api/roles`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));

        throw new Error(
          errorData?.message || `Unable to load permissions (${res.status})`,
        );
      }

      const roleData = await res.json();

      console.log("Roles received:", roleData);

      const roles = Array.isArray(roleData)
        ? roleData
        : Array.isArray(roleData?.roles)
          ? roleData.roles
          : [];

      const matchedRole = roles.find((role) => {
        const roleKey = normalizeRole(
          role?.roleKey || role?.role || role?.roleName || role?.name || "",
        );

        return roleKey === userRole;
      });

      if (!matchedRole) {
        console.warn(`No saved role permissions found for: ${userRole}`);

        localStorage.setItem("permissions", JSON.stringify({}));

        return {};
      }

      const permissions =
        matchedRole?.permissions && typeof matchedRole.permissions === "object"
          ? matchedRole.permissions
          : {};

      localStorage.setItem("permissions", JSON.stringify(permissions));

      console.log("Permissions saved:", permissions);

      return permissions;
    } catch (err) {
      console.error("Permission fetch error:", err);

      // Do not keep previous user's permissions
      localStorage.setItem("permissions", JSON.stringify({}));

      throw err;
    }
  };

  // ============================================================
  // LOGIN SUBMIT
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      // Clear stale data from previous user
      localStorage.removeItem("permissions");
      localStorage.removeItem("role");
      localStorage.removeItem("userRole");

      // --------------------------------------------------------
      // LOGIN API
      // --------------------------------------------------------

      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          email: email.trim(),
          password,
        }),
      });

      let data = {};

      try {
        data = await res.json();
      } catch {
        throw new Error("Invalid response from server");
      }

      if (!res.ok) {
        throw new Error(
          data?.message || data?.error || `Login failed (${res.status})`,
        );
      }

      if (!data?.token) {
        throw new Error("Token not received from server");
      }

      if (!data?.user) {
        throw new Error("User information not received from server");
      }

      // --------------------------------------------------------
      // SAVE TOKEN
      // --------------------------------------------------------

      localStorage.setItem("token", data.token);

      // --------------------------------------------------------
      // SAVE USER
      // --------------------------------------------------------

      localStorage.setItem("user", JSON.stringify(data.user));

      // --------------------------------------------------------
      // SAVE ROLE
      // --------------------------------------------------------

      const normalizedRole = normalizeRole(
        data.user?.role || data.user?.roleKey || data.user?.roleName || "",
      );

      if (normalizedRole) {
        // Saving both so existing code remains compatible
        localStorage.setItem("role", normalizedRole);

        localStorage.setItem("userRole", normalizedRole);
      }

      // --------------------------------------------------------
      // LOAD PERMISSIONS
      // --------------------------------------------------------

      try {
        await fetchRolePermissions(data.token, data.user);
      } catch (permissionError) {
        console.error("Could not load permissions:", permissionError);

        /*
          We intentionally do not stop login here.

          User can still log in, but permission-controlled
          modules will remain hidden until permissions can
          be loaded.
        */
      }

      // --------------------------------------------------------
      // LOGIN SUCCESS
      // --------------------------------------------------------

      onLoginSuccess(data.token, data.user);
    } catch (err) {
      console.error("Login error:", err);

      // Login failed, remove partial auth data
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("role");
      localStorage.removeItem("userRole");
      localStorage.removeItem("permissions");

      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="lg-page">
      {/* ================= BRAND PANEL ================= */}
      <section className="lg-brand">
        <div className="lg-brand-top">
          <div className="lg-logo-row">
            <div className="lg-logo-mark">
              <SofaIcon />
            </div>

            <div className="lg-logo-text">
              <strong>Softvolute Buildcon LLP</strong>
              <span>INTERIOR DESIGN</span>
            </div>
          </div>
        </div>

        <div className="lg-brand-middle">
          <h2 className="lg-headline">
            Design it. Execute it. <em>Deliver it.</em>
          </h2>

          <p className="lg-subline">
            One workspace for quotations, vendor purchase orders, payments and
            site progress, from the first design brief to the final handover.
          </p>

          <ul className="lg-feature-list">
            <li>
              <span className="lg-feature-dot">
                <CheckIcon />
              </span>
              Quotes and customer estimates
            </li>
            <li>
              <span className="lg-feature-dot">
                <CheckIcon />
              </span>
              Vendor POs and payment approvals
            </li>
            <li>
              <span className="lg-feature-dot">
                <CheckIcon />
              </span>
              Site progress and project tracking
            </li>
          </ul>
        </div>

        {/* Decorative skyline */}
        <div className="lg-skyline" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
      </section>

      {/* ================= FORM ================= */}
      <section className="lg-form-side">
        <div className="lg-card">
          <h1 className="lg-welcome">Welcome back</h1>
          <p className="lg-welcome-sub">Sign in to your workspace</p>

          {error && (
            <p className="lg-error" role="alert">
              <AlertIcon />
              <span>{error}</span>
            </p>
          )}

          <form onSubmit={handleSubmit} className="lg-form">
            {/* EMAIL */}

            <div className="lg-field">
              <span className="lg-field-icon">
                <MailIcon />
              </span>

              <input
                id="login-email"
                type="email"
                placeholder=" "
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />

              <label htmlFor="login-email">Email address</label>
            </div>

            {/* PASSWORD */}

            <div className="lg-field">
              <span className="lg-field-icon">
                <LockIcon />
              </span>

              <input
                id="login-password"
                className="lg-password-input"
                type={showPassword ? "text" : "password"}
                placeholder=" "
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />

              <label htmlFor="login-password">Password</label>

              <button
                type="button"
                className="lg-toggle-btn"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>

            {/* LOGIN BUTTON */}

            <button type="submit" className="lg-submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="lg-spinner" aria-hidden="true" />
                  Logging in...
                </>
              ) : (
                <>
                  Login
                  <ArrowIcon />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="lg-footer">
          © Softvolute Buildcon LLP. All rights reserved.
        </p>
      </section>
    </div>
  );
};

export default Login;
